
import { DOCUMENT, Location } from '@angular/common';
import { HttpEvent, HttpHandler, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService
} from '@azure/msal-angular';
import { InteractionRequiredAuthError, SilentRequest } from '@azure/msal-browser';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Injectable()
export class CustomMsalInterceptor extends MsalInterceptor {
  private refreshingToken: Promise<string> | null = null;

  constructor(
    @Inject(MSAL_INTERCEPTOR_CONFIG) private readonly interceptorConfig: MsalInterceptorConfiguration,
    private readonly msalService: MsalService,
    location: Location,
    msalBroadcastService: MsalBroadcastService,
    @Inject(DOCUMENT) document: any
  ) {
    super(interceptorConfig, msalService, location, msalBroadcastService, document);
  }

  override intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const account = this.msalService.instance.getActiveAccount() ?? undefined;
    const scopes = this.lookupScopes(req.url);

    if (!scopes || scopes.length === 0) {
      return next.handle(req);
    }

    const silentRequest: SilentRequest = {
      scopes,
      account,
    };

    return from(this.getAccessTokenWithDedup(silentRequest)).pipe(
      switchMap((accessToken: string) => {
        const authReq = req.clone({
          setHeaders: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        return next.handle(authReq);
      })
    );
  }

  private async getAccessTokenWithDedup(silentRequest: SilentRequest): Promise<string> {
    try {
      const result = await this.msalService.instance.acquireTokenSilent(silentRequest);
      return result.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        throw error;
      }

      if (!this.refreshingToken) {
        this.refreshingToken = this.msalService.instance
          .acquireTokenSilent(silentRequest)
          .then((result) => {
            this.refreshingToken = null;
            return result.accessToken;
          })
          .catch((err) => {
            this.refreshingToken = null;
            throw err;
          });
      }

      return this.refreshingToken;
    }
  }

  /**
   * Replicates getScopesForEndpoint logic using the injected config.
   */
  private lookupScopes(endpoint: string, method: string = 'GET'): string[] | null {
    const resourceMap = this.interceptorConfig.protectedResourceMap;

    for (const [key, scopesOrObject] of resourceMap.entries()) {
      const pattern = new RegExp('^' + key.replace(/\*/g, '.*') + '$');

      if (pattern.test(endpoint) && !endpoint.endsWith('/consent')) {
        if (Array.isArray(scopesOrObject)) {
          return scopesOrObject.filter((scope): scope is string => typeof scope === 'string');
        } else if (
          scopesOrObject &&
          typeof scopesOrObject === 'object' &&
          'scopes' in scopesOrObject
        ) {
          const typedScopesOrObject = scopesOrObject as { scopes: string[]; httpMethod?: string };
          // Optionally match HTTP method if needed
          if (!typedScopesOrObject.httpMethod || typedScopesOrObject.httpMethod === method.toUpperCase()) {
            return typedScopesOrObject.scopes;
          }
        }
      }
    }

    return null;
  }
}
